import AddIcon from '@mui/icons-material/Add'
import { CircularProgress, IconButton, Tooltip } from '@mui/material'
import { useEffect, useState } from 'react'
import { Link } from '../common/util/input/Link'
import {
    getAtomicAsset,
    getAtomicCollection,
    getAtomicSchema,
    getAtomicTemplate,
    getAtomicTemplateByIdOnly,
} from '../helpers/Api'
import { useTranslation } from 'react-i18next'
import BurnIndicator from '../burnindicator/BurnIndicator'

export const DisplayNftElement = ({
    collectionName,
    schema,
    templateId,
    templateName,
    image,
    assetId,
    matchAttribute,
    amount,
    odds,
    calcTotalNftOutcomesOdds,
    guaranteed,
    index,
    setSelectedUploadAsset,
    isFromPool,
    burnIngredient,
    isNoNFTOutcome,
}) => {
    const [cllctnName, setCllctnName] = useState(collectionName)
    const [collectionDisplayName, setCollectionDisplayName] = useState(null)
    const [schemaName, setSchemaName] = useState(null)
    const [tmplName, setTmpllateName] = useState(null)
    const [img, setImg] = useState(null)
    const [tmplId, setTmplId] = useState(templateId)
    const [asstId, setAsstId] = useState(null)

    const { t } = useTranslation('common')

    const fetchAndParseData = async () => {
        if (isFromPool) {
            setImg('pool_image')
        } else if (
            image &&
            collectionName &&
            schema &&
            templateName &&
            templateId &&
            assetId
        ) {
            setSchemaName(schema)
            setTmpllateName(templateName)
            setAsstId(assetId)
            setImg(
                image
                    ? image.replace('https://ipfs.hivebp.io/ipfs/', '')
                    : 'no_image',
            )
        } else if (
            image &&
            collectionName &&
            schema &&
            templateName &&
            templateId
        ) {
            setSchemaName(schema)
            setTmpllateName(templateName)
            setAsstId(assetId)
            setImg(
                image
                    ? image.replace('https://ipfs.hivebp.io/ipfs/', '')
                    : 'no_image',
            )
        } else if (collectionName && templateId) {
            const result = await getAtomicTemplate(templateId, collectionName)
            const data = result.data
            setCollectionDisplayName(data?.collection.name)
            setSchemaName(data?.schema.schema_name)
            setTmpllateName(data?.name)

            if (data?.immutable_data && data.immutable_data.img) {
                setImg(data.immutable_data.img)
            } else {
                setImg('no_image')
            }
            if (data?.immutable_data && data.immutable_data.video) {
                setImg(data.immutable_data.video)
            }
        } else if (collectionName && schema) {
            const result = await getAtomicSchema(schema, collectionName)
            const data = result.data
            setCollectionDisplayName(data.collection.name)
            setSchemaName(schema)
            setImg(data.collection.img)
        } else if (collectionName && !isNoNFTOutcome) {
            const result = await getAtomicCollection(collectionName)
            const data = result.data
            setCollectionDisplayName(data.name)
            setImg(data.img)
        } else if (templateId) {
            const result = await getAtomicTemplateByIdOnly(templateId)
            const data = result.data[0]
            setCllctnName(data.collection.collection_name)
            setCollectionDisplayName(data.collection.name)
            setSchemaName(data.schema.schema_name)
            setTmpllateName(data.name)
            if (data.immutable_data && data.immutable_data.img) {
                setImg(data.immutable_data.img)
            } else {
                setImg('no_image')
            }
            if (data.immutable_data && data.immutable_data.video) {
                setImg(data.immutable_data.video)
            }
        } else if (assetId) {
            const result = await getAtomicAsset(assetId)
            const data = result.data
            setCllctnName(data.collection.collection_name)
            setCollectionDisplayName(data.collection.name)
            setSchemaName(data.schema.schema_name)
            setTmpllateName(data.name)
            setTmplId(data.template.template_id)
            if (
                data.template.immutable_data &&
                data.template.immutable_data.img
            ) {
                setImg(data.template.immutable_data.img)
            } else {
                setImg('no_image')
            }
            if (
                data.template.immutable_data &&
                data.template.immutable_data.video
            ) {
                setImg(data.template.immutable_data.video)
            }
        } else if (isNoNFTOutcome) {
            setImg('no_outcome')
        }
    }

    useEffect(() => {
        fetchAndParseData()
    }, [collectionName, schema, templateId])

    return (
        <div className="rounded-md shadow-md w-46">
            <div className="h-60 w-46 relative bg-white dark:!bg-black rounded-t-md border-l border-t border-r border-zinc-200 dark:!border-paperl">
                {setSelectedUploadAsset ? (
                    <Tooltip
                        title={`Click here to select matching assets from your inventory`}
                    >
                        <div className="absolute z-10 top-2 right-2 bg-white dark:!bg-black rounded-full animate-pulse">
                            <IconButton
                                color="primary"
                                onClick={() => {
                                    setSelectedUploadAsset(index)
                                }}
                            >
                                <AddIcon />
                            </IconButton>
                        </div>
                    </Tooltip>
                ) : null}
                {amount ? (
                    <Tooltip title={`${amount} NFT${amount > 1 ? 's' : ''}`}>
                        <div className="absolute z-10 top-2 left-2 bg-white dark:!bg-black font-medium rounded-lg px-2">
                            {amount}
                        </div>
                    </Tooltip>
                ) : null}
                {guaranteed ? (
                    <Tooltip
                        title={t(
                            'craft.the_chance_that_the_user_will_get_this_nft_is_x',
                            { x: 100 },
                        )}
                    >
                        <div className="absolute z-10 top-2 left-2 bg-white dark:!bg-black font-medium rounded-lg px-2">
                            100%
                        </div>
                    </Tooltip>
                ) : odds ? (
                    <Tooltip
                        title={t(
                            'craft.the_chance_that_the_user_will_get_this_nft_is_x',
                            { x: (odds / calcTotalNftOutcomesOdds()) * 100 },
                        )}
                    >
                        <div className="absolute z-10 top-2 left-2 bg-white dark:!bg-black font-medium rounded-lg px-2">
                            {parseInt(
                                (odds / calcTotalNftOutcomesOdds()) * 100,
                            )}
                            %
                        </div>
                    </Tooltip>
                ) : null}
                {burnIngredient ? (
                    <div className="h-full w-full rounded-t-md">
                        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 font-bold text-center">
                            <BurnIndicator />
                        </div>
                    </div>
                ) : img === null ? (
                    <div className="h-full w-full rounded-t-md">
                        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2">
                            <CircularProgress />
                        </div>
                    </div>
                ) : img === 'no_image' ? (
                    <div className="h-full w-full rounded-t-md">
                        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 font-bold text-center">
                            <i>invalid image</i>
                        </div>
                    </div>
                ) : img === 'pool_image' ? (
                    <img
                        className="h-full w-full object-fill rounded-t-md bg-zinc-300"
                        src={`https://ipfs.hivebp.io/ipfs/QmUQyQYmSAwdQLK3QyfMrASkd668sgB1GS3SLoV6F6a7A9`}
                    />
                ) : !isNoNFTOutcome ? (
                    <img
                        className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2"
                        src={`https://ipfs.hivebp.io/preview?collection=${cllctnName}&size=320&hash=${
                            img ? img.replace('video:', '') : img
                        }`}
                    />
                ) : img === 'no_outcome' ? (
                    <div className="h-full w-full rounded-t-md">
                        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 font-bold text-center">
                            <i>No outcome</i>
                        </div>
                    </div>
                ) : (
                    ''
                )}
            </div>

            <div className="w-46 bg-white dark:!bg-black rounded-b-md text-center p-3 border-l border-b border-r border-zinc-200 dark:!border-paperl">
                {burnIngredient ? (
                    t('craft.burns_ingredient_x', { x: burnIngredient })
                ) : !isFromPool ? (
                    <>
                        {cllctnName && !isNoNFTOutcome ? (
                            <Tooltip
                                title={
                                    <div>
                                        NFT is from the <b>{cllctnName}</b>{' '}
                                        collection
                                    </div>
                                }
                                disableTouchListener
                                placement="right"
                                arrow
                            >
                                <div className="hover:text-primary">
                                    <Link
                                        href={`/collection/${cllctnName}`}
                                        external={true}
                                    >
                                        {collectionDisplayName
                                            ? collectionDisplayName
                                            : cllctnName}
                                    </Link>
                                </div>
                            </Tooltip>
                        ) : !isNoNFTOutcome ? (
                            <div>{`<loading..a.>`}</div>
                        ) : (
                            <div className={'h-6'} />
                        )}
                        {schemaName ? (
                            <Tooltip
                                title={
                                    <div>
                                        NFT belongs to the <b>{schemaName}</b>{' '}
                                        schema
                                    </div>
                                }
                                disableTouchListener
                                placement="right"
                                arrow
                            >
                                <div className="hover:text-primary">
                                    <Link
                                        href={`/collection/${cllctnName}/${schemaName}`}
                                        external={true}
                                    >
                                        {schemaName}
                                    </Link>
                                </div>
                            </Tooltip>
                        ) : !isNoNFTOutcome ? (
                            <Tooltip
                                title={`NFT belongs to any schema of the collection`}
                                disableTouchListener
                                placement="right"
                                arrow
                            >
                                <div>{`<any schema>`}</div>
                            </Tooltip>
                        ) : (
                            <div className={'h-6'}>{``}</div>
                        )}
                        {tmplName || tmplId ? (
                            <Tooltip
                                title={
                                    <div>
                                        NFT has the template ID <b>{tmplId}</b>
                                    </div>
                                }
                                disableTouchListener
                                placement="right"
                                arrow
                            >
                                <div className="hover:text-primary">
                                    <Link
                                        href={`/template/${tmplId}`}
                                        external={true}
                                    >
                                        {tmplName ? tmplName : tmplId}
                                    </Link>
                                </div>
                            </Tooltip>
                        ) : matchAttribute &&
                          matchAttribute.attribute_name !== '' ? (
                            <Tooltip
                                title={
                                    <div>
                                        NFT has an attribute{' '}
                                        <b>{matchAttribute.attribute_name}</b>{' '}
                                        with the value{' '}
                                        <b>{matchAttribute.values[0]}</b>
                                    </div>
                                }
                                disableTouchListener
                                placement="right"
                                arrow
                            >
                                <div>
                                    {matchAttribute.attribute_name} -{' '}
                                    {matchAttribute.values[0]}
                                </div>
                            </Tooltip>
                        ) : !isNoNFTOutcome ? (
                            <Tooltip
                                title={`NFT belongs to any template of the collection`}
                                disableTouchListener
                                placement="right"
                                arrow
                            >
                                <div>{'<any template>'}</div>
                            </Tooltip>
                        ) : (
                            <div className={'h-6'} />
                        )}
                        {asstId ? (
                            <Tooltip
                                title={
                                    <div>
                                        NFT belongs has the assetId{' '}
                                        <b>{asstId}</b>
                                    </div>
                                }
                                disableTouchListener
                                placement="right"
                                arrow
                            >
                                <div className="hover:text-primary">
                                    <Link
                                        href={`/asset/${asstId}`}
                                        external={true}
                                    >
                                        {asstId}
                                    </Link>
                                </div>
                            </Tooltip>
                        ) : null}{' '}
                    </>
                ) : (
                    <div>{t('craft.select_from_pool_of_preminted_nfts')}</div>
                )}
            </div>
        </div>
    )
}
